import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Settings, 
  PiggyBank, Target, Shield, Brain, ChevronUp, ChevronDown 
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface BankrollSettings {
  total_bankroll: number;
  risk_tolerance: string;
  max_stake_percent: number;
  daily_loss_limit: number | null;
  weekly_loss_limit: number | null;
  monthly_loss_limit: number | null;
  auto_stake_sizing: boolean;
  notifications_enabled: boolean;
}

interface BankrollHistory {
  id: string;
  balance: number;
  change_amount: number;
  change_type: string;
  notes: string;
  recorded_at: string;
}

interface StakeRecommendation {
  recommended_stake: number;
  stake_percent: number;
  risk_score: string;
  reasoning: string;
}

const riskProfiles = {
  conservative: { 
    label: 'Conservative', 
    description: 'Lower risk, smaller bets, focus on preservation',
    color: 'text-blue-500',
    maxPercent: 2 
  },
  medium: { 
    label: 'Balanced', 
    description: 'Moderate risk, balanced approach',
    color: 'text-yellow-500',
    maxPercent: 5 
  },
  aggressive: { 
    label: 'Aggressive', 
    description: 'Higher risk, larger potential returns',
    color: 'text-red-500',
    maxPercent: 10 
  },
};

export function BankrollManager() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<BankrollSettings | null>(null);
  const [history, setHistory] = useState<BankrollHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Form state
  const [bankroll, setBankroll] = useState(1000);
  const [riskTolerance, setRiskTolerance] = useState('medium');
  const [maxStake, setMaxStake] = useState(5);
  const [dailyLimit, setDailyLimit] = useState<number | ''>('');
  const [autoSizing, setAutoSizing] = useState(true);
  const [notifications, setNotifications] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBankrollData();
    }
  }, [user]);

  const fetchBankrollData = async () => {
    try {
      // Fetch settings
      const { data: settingsData } = await supabase
        .from('bankroll_settings')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (settingsData) {
        setSettings(settingsData);
        setBankroll(settingsData.total_bankroll);
        setRiskTolerance(settingsData.risk_tolerance);
        setMaxStake(settingsData.max_stake_percent);
        setDailyLimit(settingsData.daily_loss_limit || '');
        setAutoSizing(settingsData.auto_stake_sizing);
        setNotifications(settingsData.notifications_enabled);
      }

      // Fetch history
      const { data: historyData } = await supabase
        .from('bankroll_history')
        .select('*')
        .eq('user_id', user?.id)
        .order('recorded_at', { ascending: false })
        .limit(20);

      setHistory(historyData || []);
    } catch (error) {
      console.error('Error fetching bankroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!user) return;
    setSaving(true);

    try {
      const settingsData = {
        user_id: user.id,
        total_bankroll: bankroll,
        risk_tolerance: riskTolerance,
        max_stake_percent: maxStake,
        daily_loss_limit: dailyLimit || null,
        auto_stake_sizing: autoSizing,
        notifications_enabled: notifications,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('bankroll_settings')
        .upsert(settingsData, { onConflict: 'user_id' });

      if (error) throw error;

      // Record initial balance if new
      if (!settings) {
        await supabase.from('bankroll_history').insert({
          user_id: user.id,
          balance: bankroll,
          change_amount: bankroll,
          change_type: 'deposit',
          notes: 'Initial bankroll setup',
        });
      }

      toast.success('Bankroll settings saved!');
      setShowSettings(false);
      fetchBankrollData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateBankroll = async (amount: number, type: 'deposit' | 'withdrawal') => {
    if (!user || !settings) return;

    const newBalance = type === 'deposit' 
      ? settings.total_bankroll + amount 
      : settings.total_bankroll - amount;

    if (newBalance < 0) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      await supabase
        .from('bankroll_settings')
        .update({ total_bankroll: newBalance, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);

      await supabase.from('bankroll_history').insert({
        user_id: user.id,
        balance: newBalance,
        change_amount: type === 'deposit' ? amount : -amount,
        change_type: type,
      });

      toast.success(`${type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded`);
      fetchBankrollData();
    } catch (error) {
      toast.error('Failed to update bankroll');
    }
  };

  // Calculate stats
  const currentBalance = settings?.total_bankroll || bankroll;
  const recommendedStake = currentBalance * (maxStake / 100);
  const dailyPnL = history
    .filter(h => new Date(h.recorded_at).toDateString() === new Date().toDateString())
    .reduce((acc, h) => acc + (h.change_amount || 0), 0);
  const weeklyPnL = history
    .filter(h => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(h.recorded_at) > weekAgo;
    })
    .reduce((acc, h) => acc + (h.change_amount || 0), 0);

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // First time setup
  if (!settings && !showSettings) {
    return (
      <Card className="bg-gradient-to-br from-primary/20 via-card to-card border-primary/30">
        <CardContent className="p-8 text-center">
          <PiggyBank className="h-16 w-16 mx-auto text-primary mb-4" />
          <h3 className="text-2xl font-bold mb-2">AI Bankroll Manager</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Get personalized staking advice based on your risk tolerance and betting history.
          </p>
          <Button size="lg" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Set Up Your Bankroll
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Overview */}
      <Card className="bg-gradient-to-br from-green-500/20 via-card to-card border-green-500/30">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Wallet className="h-4 w-4" />
                <span>Current Bankroll</span>
              </div>
              <div className="text-4xl font-bold">${currentBalance.toLocaleString()}</div>
              <div className="flex items-center gap-4 mt-2">
                <span className={`flex items-center gap-1 text-sm ${dailyPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {dailyPnL >= 0 ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  ${Math.abs(dailyPnL).toFixed(2)} today
                </span>
                <span className={`flex items-center gap-1 text-sm ${weeklyPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {weeklyPnL >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  ${Math.abs(weeklyPnL).toFixed(2)} this week
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSettings(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showSettings ? (
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Bankroll Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Total Bankroll */}
            <div className="space-y-2">
              <Label>Total Bankroll ($)</Label>
              <Input
                type="number"
                value={bankroll}
                onChange={(e) => setBankroll(Number(e.target.value))}
                min={0}
              />
            </div>

            {/* Risk Tolerance */}
            <div className="space-y-3">
              <Label>Risk Tolerance</Label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(riskProfiles).map(([key, profile]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setRiskTolerance(key);
                      setMaxStake(profile.maxPercent);
                    }}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      riskTolerance === key 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`font-semibold ${profile.color}`}>{profile.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{profile.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Max Stake Percent */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <Label>Max Stake Per Bet</Label>
                <span className="text-sm font-medium">{maxStake}% (${(bankroll * maxStake / 100).toFixed(2)})</span>
              </div>
              <Slider
                value={[maxStake]}
                onValueChange={([val]) => setMaxStake(val)}
                min={1}
                max={15}
                step={0.5}
              />
            </div>

            {/* Daily Loss Limit */}
            <div className="space-y-2">
              <Label>Daily Loss Limit ($)</Label>
              <Input
                type="number"
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value ? Number(e.target.value) : '')}
                placeholder="Optional"
              />
            </div>

            {/* Toggles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto Stake Sizing</Label>
                  <p className="text-xs text-muted-foreground">AI suggests optimal stake for each bet</p>
                </div>
                <Switch checked={autoSizing} onCheckedChange={setAutoSizing} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Risk Alerts</Label>
                  <p className="text-xs text-muted-foreground">Get notified when approaching limits</p>
                </div>
                <Switch checked={notifications} onCheckedChange={setNotifications} />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSettings(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveSettings} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="recommendations" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="recommendations" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              AI Advice
            </TabsTrigger>
            <TabsTrigger value="limits" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Limits
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recommendations" className="space-y-4">
            <Card className="bg-card/50 border-border/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/20">
                    <Brain className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Recommended Stake Range</h4>
                    <div className="text-2xl font-bold text-primary mb-2">
                      ${(currentBalance * 0.01).toFixed(2)} - ${recommendedStake.toFixed(2)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Based on your {riskProfiles[riskTolerance as keyof typeof riskProfiles].label.toLowerCase()} risk profile and ${currentBalance.toLocaleString()} bankroll, we recommend betting between 1-{maxStake}% per bet.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-green-500" />
                    <span className="font-medium">Low Risk Bet</span>
                  </div>
                  <div className="text-xl font-bold">${(currentBalance * 0.01).toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">1% of bankroll</p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <span className="font-medium">Max Recommended</span>
                  </div>
                  <div className="text-xl font-bold">${recommendedStake.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">{maxStake}% of bankroll</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="limits" className="space-y-4">
            <Card className="bg-card/50 border-border/50">
              <CardContent className="p-6 space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Daily Loss Limit</span>
                    <span className="font-medium">
                      {settings?.daily_loss_limit ? `$${settings.daily_loss_limit}` : 'Not set'}
                    </span>
                  </div>
                  {settings?.daily_loss_limit && (
                    <Progress 
                      value={Math.min((Math.abs(dailyPnL) / settings.daily_loss_limit) * 100, 100)} 
                      className="h-2"
                    />
                  )}
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span>Risk Profile</span>
                    <Badge className={riskProfiles[riskTolerance as keyof typeof riskProfiles].color}>
                      {riskProfiles[riskTolerance as keyof typeof riskProfiles].label}
                    </Badge>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between">
                    <span>Max Stake Per Bet</span>
                    <span className="font-medium">{maxStake}% (${recommendedStake.toFixed(2)})</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card className="bg-card/50 border-border/50">
              <CardContent className="p-4">
                {history.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No history yet</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div>
                          <p className="font-medium capitalize">{item.change_type.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(item.recorded_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={item.change_amount >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {item.change_amount >= 0 ? '+' : ''}${item.change_amount.toFixed(2)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Balance: ${item.balance.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
